/**
 * This function will measure the incoming text and split it into lines based on the max width of the text marker
 * @param text The text to wrap
 * @param fontSize The font size of the text marker
 * @param fontFamily The font family of the text marker
 * @param textEditor The contenteditable div of the text marker to apply styles to for better UX.
 * @param textWidthContext The CanvasContext used to check the width of the text
 * @returns The wrapped text with newline characters
 */
export const wrapTextByWidth = (
	text: string,
	fontSize: number,
	fontFamily: string,
	textEditor: HTMLTextAreaElement | null,
	textWidthContext: CanvasRenderingContext2D
): string => {
	// Add an empty space so that the editable area does not turn into a tiny circle.
	// Set the line height to prevent the offcentered cursor. Not sure if this is the best way, may revisit in the future
	// and override the contenteditable behavior that adds <br> when there is no text

	if ((!text || text.trim() === "") && textEditor) {
		textEditor.style.lineHeight = "44px";
		return "Text";
	}

	if (textEditor) {
		textEditor.style.lineHeight = "1em";
	}

	const maxWidth: number = 300;
	const context = textWidthContext;

	if (context) {
		text = text.split("\r\n").join(" ");
		context.font = `${fontSize}px ${fontFamily}`;

		const words = text.split(" ");
		let line: string = "";
		let forceLongWordBreak: boolean = false;
		const lines: Array<string> = new Array<string>();

		for (const word of words) {
			const testLine = line + (line ? " " : "") + word;
			const { width } = context.measureText(testLine);
			const wordWidth = context.measureText(word).width;

			if (wordWidth > maxWidth) {
				forceLongWordBreak = true;
				const wordLines = splitLongWord(word, fontSize, fontFamily, textWidthContext);
				lines.push(...wordLines);
			} else if (width > maxWidth && line) {
				lines.push(line);
				line = word;
			} else {
				line = testLine;
			}
		}

		if (line) lines.push(line);

		if (textEditor) {
			if (lines.length > 1 || forceLongWordBreak) {
				textEditor.style.wordBreak = "break-word";
				textEditor.style.whiteSpace = "pre-wrap";
			} else {
				textEditor.style.wordBreak = "unset";
				textEditor.style.whiteSpace = "nowrap";
			}
		}

		return lines.join("\r\n").trim();
	}

	return text;
};

/**
 *
 * @param text Splits a very long continuous string into multiple lines
 * @param text The text to wrap
 * @param fontSize The font size of the text marker
 * @param fontFamily The font family of the text marker
 * @param textWidthContext The CanvasContext used to check the width of the text
 * @returns An array of words
 */
const splitLongWord = (text: string, fontSize: number, fontFamily: string, textWidthContext: CanvasRenderingContext2D): Array<string> => {
	const maxWidth: number = 300;
	const context = textWidthContext;
	const lines: string[] = [];
	let currentLine = "";

	if (context) {
		for (let i = 0; i < text.length; i++) {
			const testLine = `${currentLine}${text[i]}`;
			const testWidth = context.measureText(testLine).width;

			if (testWidth > maxWidth && currentLine.length > 0) {
				lines.push(currentLine);
				currentLine = text[i]; // start new line with current char
			} else {
				currentLine = testLine;
			}
		}

		if (currentLine) lines.push(currentLine);
	}

	return lines;
};
